import type { Dispatch, SetStateAction } from 'react';

import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { Endpoints } from '@xver/apis';
import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import { useAppSelector } from '@xver/hooks/useAppSelector';
import { requestAuthUser } from '@xver/store/auth/auth.actions';
import { setErrorMessage } from '@xver/store/error/error.actions';
import storage from '@xver/utils/storage';

import { PAGE_TYPE, PageType } from '../../../constants';
import { useResendCountdown } from '../../PhoneNumberVerificationForm/hooks/useResendCountdown';

const RESEND_TIMEOUT_MS = 10 * 60 * 1000;

interface useTwoFactorVerificationProps {
    setPageType: Dispatch<SetStateAction<PageType>>;
}

export function useTwoFactorVerification({ setPageType }: useTwoFactorVerificationProps) {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation('notifications');

    const { email2FA: email, password2FA: password } = useAppSelector((state) => state.authPage);

    const {
        formState: { isSubmitting },
        setValue,
        watch,
    } = useForm({
        defaultValues: {
            otpCode: '',
        },
    });

    const {
        isAvailable: isResendAvailable,
        formattedRemainingTime: resendCountdown,
        restart,
    } = useResendCountdown({ initialDurationMs: RESEND_TIMEOUT_MS });

    const verify = useCallback(
        async (otpCode: string) => {
            if (!email || !password) {
                dispatch(setErrorMessage(t('auth.missingCredentials')));
                setPageType(PAGE_TYPE.SIGN_IN);
                return;
            }

            try {
                const response = await Endpoints.v1.Authentication.verify2FA({
                    email,
                    password,
                    otpCode,
                });

                if (response.data?.token) {
                    storage.set('_zugang', response.data.token);
                    await dispatch(requestAuthUser());
                    navigate('/dashboard', { replace: true });
                }
            } catch ({ message }: any) {
                dispatch(setErrorMessage(message as string));
                setValue('otpCode', '');
            }
        },
        [email, password, dispatch, navigate, setPageType, setValue, t]
    );

    const handleOtpChange = useCallback(
        (value: string) => {
            setValue('otpCode', value);

            if (value.length === 6) {
                verify(value);
            }
        },
        [setValue, verify]
    );

    const handleResend = useCallback(async () => {
        if (!isResendAvailable || !email) return;

        try {
            await Endpoints.v1.Authentication.send2FACode({ email });
            toast.success(t('auth.verificationSentMessage'));
            restart();
        } catch ({ message }: any) {
            toast.error(message as string);
        }
    }, [isResendAvailable, email, restart, t]);

    useEffect(() => {
        if (!email || !password) {
            setPageType(PAGE_TYPE.SIGN_IN);
            return;
        }

        Endpoints.v1.Authentication.send2FACode({ email }).catch(({ message }: any) => {
            toast.error(message as string);
        });
    }, [email, password, setPageType]);

    return {
        isSubmitting,
        setValue,
        watch,
        handleOtpChange,
        isResendAvailable,
        resendCountdown,
        handleResend,
    };
}
